import { useCallback, useEffect, useState } from "react";

export type Theme = "light" | "dark";

const STORAGE_KEY = "zenfresh-theme";

function getInitialTheme(): Theme {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/**
 * Mirrors the same logic as index.html's pre-hydration inline script (which
 * sets document.documentElement.dataset.theme before first paint, so there
 * is no flash-of-wrong-theme to fix here) — this hook is what keeps that
 * attribute in sync after mount and exposes the toggle.
 */
export default function useTheme() {
  const [theme, setTheme] = useState<Theme>(getInitialTheme);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
  }, [theme]);

  useEffect(() => {
    // Only follow a live OS-preference change if the visitor never made an
    // explicit choice — once they toggle, that choice persists regardless
    // of what their OS setting later does. Deliberately does NOT write to
    // localStorage here (only toggleTheme does) — writing on every mount
    // would make this guard see a stored value immediately after the very
    // first render and permanently disable itself for a visitor who never
    // actually chose anything.
    if (localStorage.getItem(STORAGE_KEY)) return;
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e: MediaQueryListEvent) => setTheme(e.matches ? "dark" : "light");
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((t) => {
      const next: Theme = t === "dark" ? "light" : "dark";
      localStorage.setItem(STORAGE_KEY, next);
      return next;
    });
  }, []);

  return { theme, toggleTheme };
}
